import { useMemo } from "react";
import { useFutureTipsQuery } from "@/api/vercel";
import {
  buildEarlyMailTipsLookupFromFutureTipsResponse,
  extractFutureTipsPayload,
  normalizeTrackNameForLookup,
} from "@/utils/future-tips-utils";
import { logger } from "@/utils/logger";
import { calendarDaysBetween, toInstant } from "@/utils/time-format";

/**
 * Early Mail (future-tips) for one race, as shown under the race tips on /results and /todays-tips.
 *
 * @param {Object} params
 * @param {string} params.date - Race date, DD-MM-YYYY
 * @param {string} params.trackName
 * @param {string|number} params.raceNumber
 * @param {Date|string} params.viewedDate - Race day if `date` is missing, for the "N days prior" label
 * @param {boolean} [params.enabled=true]
 */
export const useEarlyMailForRace = ({
  date,
  trackName,
  raceNumber,
  viewedDate,
  enabled = true,
}) => {
  // Fetch Early Mail tips (future-tips) for the selected date
  const {
    data: futureTipsResponse,
    isLoading: isEarlyMailLoading,
    isError: isEarlyMailError,
  } = useFutureTipsQuery({
    date,
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const earlyMailTipsLookup = useMemo(() => {
    // Cached data can outlive `enabled` (e.g. free users), so gate on it too
    if (!enabled || !futureTipsResponse) return new Map();
    try {
      return buildEarlyMailTipsLookupFromFutureTipsResponse(futureTipsResponse);
    } catch (err) {
      logger.error("[EarlyMail] Failed to build early mail tips lookup", err);
      return new Map();
    }
  }, [enabled, futureTipsResponse]);

  const earlyMailTips = useMemo(() => {
    if (!trackName || !raceNumber) return [];
    const key = `${normalizeTrackNameForLookup(trackName)}|${String(raceNumber).trim()}`;
    const tips = earlyMailTipsLookup?.get?.(key);
    return Array.isArray(tips) ? tips : [];
  }, [earlyMailTipsLookup, trackName, raceNumber]);

  // When this race's Early Mail arrived: `at` = the time shown (backend "HH:mm"),
  // `on` = the instant it was saved (race.updatedAt), used for "N days prior".
  const earlyMailArrival = useMemo(() => {
    if (!enabled || !futureTipsResponse || !trackName || !raceNumber)
      return null;
    try {
      const payload = extractFutureTipsPayload(futureTipsResponse);
      const tracks = Array.isArray(payload?.tracks) ? payload.tracks : [];
      const targetTrackKey = normalizeTrackNameForLookup(trackName);
      const track = tracks.find((t) => {
        const raw = t?.trackName || t?.track || t?.name || "";
        return normalizeTrackNameForLookup(raw) === targetTrackKey;
      });
      if (!track) return null;
      const races = Array.isArray(track?.races) ? track.races : [];
      const targetRace = String(raceNumber).trim();
      const race = races.find((r) => {
        const n = r?.race ?? r?.raceNumber ?? r?.number ?? r?.id ?? "";
        return String(n).trim() === targetRace;
      });
      if (!race) return null;

      const at =
        race?.tipUpdatedAt ??
        race?.tip_updated_at ??
        race?.updatedAt ??
        race?.updated_at ??
        race?.generatedAt ??
        race?.generated_at ??
        payload?.tipUpdatedAt ??
        payload?.generatedAt ??
        payload?.updatedAt ??
        null;
      const on =
        [
          race?.updatedAt,
          race?.updated_at,
          race?.generatedAt,
          race?.generated_at,
          payload?.generatedAt,
          payload?.updatedAt,
          at,
        ]
          .map(toInstant)
          .find(Boolean) ?? null;
      return { at, on };
    } catch (err) {
      logger.error(
        "[EarlyMail] Failed to extract early mail arrived time",
        err,
      );
      return null;
    }
  }, [enabled, futureTipsResponse, trackName, raceNumber]);

  const earlyMailArrivedAt = earlyMailArrival?.at ?? null;

  const earlyMailRelativeLabel = useMemo(() => {
    // Early Mail lands up to 3 days out (Saturday's can arrive on Wednesday).
    // Count the Sydney calendar days from the day it arrived to race day; if
    // the arrival day is unknown, don't guess a number.
    const fallback = "before race day";
    const arrivedOn = earlyMailArrival?.on;
    const raceDay = date || viewedDate;
    if (!arrivedOn || !raceDay) return fallback;
    const diff = calendarDaysBetween(arrivedOn, raceDay);
    if (diff == null) return fallback;
    return diff > 0 ? `${diff} day${diff === 1 ? "" : "s"} prior` : "";
  }, [earlyMailArrival, date, viewedDate]);

  return {
    earlyMailTips,
    earlyMailArrivedAt,
    earlyMailRelativeLabel,
    isEarlyMailLoading,
    isEarlyMailError,
  };
};
